"use client"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { Button } from "@/components/ui/button"
import type { Service } from "@/lib/types/service.types"
import { X } from "lucide-react"
import { useState } from "react"

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} mins`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (m === 0) return `${h} hr${h > 1 ? "s" : ""}`
  return `${h} hr${h > 1 ? "s" : ""}, ${m} mins`
}

function toPriceLabel(price: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(price)
}

function toAudienceLabel(audience: string): string {
  const map: Record<string, string> = {
    female: "Female only",
    male: "Male only",
    unisex: "Unisex",
    children: "Children",
  }
  return map[audience] ?? audience
}

type ServiceSelectionModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  parent: Service
  childServices: Service[]
  selectedChildId: number | null
  onSelect: (child: Service) => void
  onRemove: () => void
}

export function ServiceSelectionModal({
  open,
  onOpenChange,
  parent,
  childServices,
  selectedChildId,
  onSelect,
  onRemove,
}: ServiceSelectionModalProps) {
  const [expandedDesc, setExpandedDesc] = useState(false)
  const selectedChild = childServices.find((c) => c.id === selectedChildId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-h-[90dvh] max-w-2xl overflow-y-auto">
        <DialogHeader className="relative pr-8 text-left">
          <DialogTitle className="pr-6 text-left">{parent.service_name}</DialogTitle>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-11 w-11"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {toAudienceLabel(parent.target_audience)}
          </p>
          <div
            className={`text-sm text-muted-foreground [&_a]:text-primary [&_a]:underline [&_p]:mb-2 last:[&_p]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_strong]:font-semibold ${
              expandedDesc ? "" : "line-clamp-3"
            }`}
          >
            {parent.service_description ? (
              <div dangerouslySetInnerHTML={{ __html: parent.service_description }} />
            ) : null}
            {!expandedDesc && (parent.service_description?.length ?? 0) > 160 ? (
              <button
                type="button"
                className="mt-1 text-primary hover:underline"
                onClick={() => setExpandedDesc(true)}
              >
                Read more
              </button>
            ) : null}
          </div>

          <div className="space-y-2">
            <h4 className="font-medium text-foreground">Select an option *</h4>
            <div className="space-y-2">
              {childServices.map((child) => {
                const isSelected = selectedChildId === child.id
                const duration = formatDuration(child.service_duration)
                const audience = toAudienceLabel(child.target_audience)
                const details = [duration, audience].filter(Boolean).join(" • ")
                return (
                  <button
                    key={child.id}
                    type="button"
                    onClick={() => {
                      onSelect(child)
                      onOpenChange(false)
                    }}
                    className={`flex min-h-11 w-full items-center justify-between rounded-lg border p-4 text-left transition-colors ${
                      isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div>
                      <p className="font-medium text-foreground">{child.service_name}</p>
                      <p className="text-sm text-muted-foreground">{details}</p>
                      <p className="mt-1 font-semibold text-foreground">
                        {child.service_price > 0
                          ? `from ${toPriceLabel(child.service_price)}`
                          : toPriceLabel(child.service_price)}
                      </p>
                    </div>
                    <div
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected ? "border-primary bg-primary" : "border-muted-foreground"
                      }`}
                    >
                      {isSelected && <div className="h-2 w-2 rounded-full bg-primary-foreground" />}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {selectedChild && (
            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="font-medium text-foreground">
                {toPriceLabel(selectedChild.service_price)} · {formatDuration(selectedChild.service_duration)}
              </h4>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={onRemove}>
                  Remove
                </Button>
                <Button type="button" className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
